import { useAuth, API, BACKEND_URL } from "@/App";
import AppShell from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Globe, Code, FileText, Send, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ApiDocsPage() {
  const webhookUrl = `${BACKEND_URL}/api/webhook/generate`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const curlExample = `curl -X POST "${webhookUrl}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_id": "YOUR_TEMPLATE_ID",
    "variables": {
      "custName": "John Doe",
      "testDate": "2026-02-28",
      "inspector": "Jane Smith"
    }
  }' --output generated.pdf`;

  const pythonExample = `import requests

response = requests.post(
    "${webhookUrl}",
    json={
        "template_id": "YOUR_TEMPLATE_ID",
        "variables": {
            "custName": "John Doe",
            "testDate": "2026-02-28",
            "inspector": "Jane Smith"
        }
    }
)

# Save the generated PDF
with open("generated.pdf", "wb") as f:
    f.write(response.content)

# Get the document ID from headers
doc_id = response.headers.get("X-Document-Id")
print(f"Document ID: {doc_id}")`;

  const jsExample = `const response = await fetch("${webhookUrl}", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    template_id: "YOUR_TEMPLATE_ID",
    variables: {
      custName: "John Doe",
      testDate: "2026-02-28",
      inspector: "Jane Smith"
    }
  })
});

const blob = await response.blob();
const docId = response.headers.get("X-Document-Id");`;

  const requestBody = `{
  "template_id": "string (optional)",
  "template_name": "string (optional)",
  "variables": {
    "variableName": "value",
    "anotherVar": "another value"
  }
}`;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto p-6 lg:p-8" data-testid="api-docs-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            API Documentation
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Use the webhook endpoint to generate filled PDFs programmatically
          </p>
        </div>

        {/* Endpoint Card */}
        <Card className="mb-6" data-testid="endpoint-card">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-600 text-white hover:bg-emerald-700">POST</Badge>
              <code className="font-mono text-sm bg-slate-100 px-3 py-1 rounded-md flex-1 break-all">
                {webhookUrl}
              </code>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(webhookUrl)} data-testid="copy-url-button">
                <Copy size={14} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600">
              Generate a filled PDF by providing a template ID (or name) and variable values.
              The response is the generated PDF file. The document ID is returned in the <code className="font-mono text-xs bg-slate-100 px-1 rounded">X-Document-Id</code> response header.
            </p>
          </CardContent>
        </Card>

        {/* Request / Response */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card data-testid="request-body-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Send size={14} strokeWidth={1.5} />
                Request Body
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative">
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-x-auto">
                  {requestBody}
                </pre>
                <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6 text-slate-400 hover:text-white" onClick={() => copyToClipboard(requestBody)}>
                  <Copy size={12} />
                </Button>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <p className="text-slate-600">
                  <strong>Note:</strong> Provide either <code className="font-mono text-xs bg-slate-100 px-1 rounded">template_id</code> or <code className="font-mono text-xs bg-slate-100 px-1 rounded">template_name</code> (not both required).
                </p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="response-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <FileText size={14} strokeWidth={1.5} />
                Response
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Badge variant="outline" className="mb-2">200 OK</Badge>
                <p className="text-sm text-slate-600">
                  Returns the generated PDF as a binary stream (<code className="font-mono text-xs bg-slate-100 px-1 rounded">application/pdf</code>).
                </p>
              </div>
              <Separator />
              <div>
                <p className="text-xs font-medium text-slate-700 mb-1">Response Headers:</p>
                <pre className="bg-slate-50 p-2 rounded text-xs font-mono">
{`Content-Type: application/pdf
Content-Disposition: attachment; filename=generated_xxx.pdf
X-Document-Id: <uuid>`}
                </pre>
              </div>
              <Separator />
              <div>
                <Badge variant="outline" className="text-red-600 border-red-200 mb-2">400 / 404</Badge>
                <pre className="bg-slate-50 p-2 rounded text-xs font-mono">
{`{ "detail": "Template not found" }`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Code Examples */}
        <Card data-testid="code-examples-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Code size={14} strokeWidth={1.5} />
              Code Examples
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="curl">
              <TabsList>
                <TabsTrigger value="curl" data-testid="tab-curl">cURL</TabsTrigger>
                <TabsTrigger value="python" data-testid="tab-python">Python</TabsTrigger>
                <TabsTrigger value="javascript" data-testid="tab-javascript">JavaScript</TabsTrigger>
              </TabsList>
              <TabsContent value="curl">
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {curlExample}
                  </pre>
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6 text-slate-400 hover:text-white" onClick={() => copyToClipboard(curlExample)}>
                    <Copy size={12} />
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="python">
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {pythonExample}
                  </pre>
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6 text-slate-400 hover:text-white" onClick={() => copyToClipboard(pythonExample)}>
                    <Copy size={12} />
                  </Button>
                </div>
              </TabsContent>
              <TabsContent value="javascript">
                <div className="relative">
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                    {jsExample}
                  </pre>
                  <Button variant="ghost" size="icon" className="absolute top-2 right-2 h-6 w-6 text-slate-400 hover:text-white" onClick={() => copyToClipboard(jsExample)}>
                    <Copy size={12} />
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
